// How long landInPanel() waits for the panels to stop opening and closing before it
// moves focus anyway: about a second at 60 frames a second. x-collapse takes 250ms.
const LAND_WAIT_FRAMES = 60;

export default () => ({
    activePanel: '',
    // The panel views, read from the root's data-accordion on every use. A panel the
    // server adds while the accordion is hidden is included: Site settings adds
    // Allowlist Management once a URL is entered. One added while the accordion is
    // showing is missing: Livewire's morph leaves every attribute alone on an element
    // whose x-show is showing (patchAttributes() in livewire.js), so data-accordion
    // keeps the old list. See components/accordion.blade.php for why the server's
    // values are not passed through x-data.
    get panels() {
        return this.serverConfig().panels ?? [];
    },
    serverConfig() {
        return JSON.parse(this.$root.dataset.accordion || '{}');
    },
    init() {
        this.activePanel = this.serverConfig().activePanel || this.panels[0];
    },
    // Open a specific panel and move focus into it when any element dispatches
    // open-accordion-panel with the panel's view name, e.g. a Livewire
    // $this->dispatch('open-accordion-panel', panel: <view>).
    // components/accordion.blade.php calls this from an x-on on its root, as it
    // handles the allowlist events.
    //
    // With focusError (Site\Edit after a failed save) the target is the
    // panel's first validation error, the <p data-input-error> that
    // components/input-error.blade.php renders, so the user lands on what
    // to fix. When no field shows one, the panel's [data-panel-error] alert
    // is next: a refused login shows the Login panel's "Could not
    // authenticate" alert and no field error. A panel showing neither
    // falls back to its header. The lookup
    // starts a frame later, once the panels have begun to open and close,
    // which also puts it after the morph that renders the errors: Livewire
    // 3.6 delivers a server dispatch before that morph (3.7.13 after it).
    openPanelFromEvent(e) {
        const panel = e?.detail?.panel;
        if (!panel || !this.panels.includes(panel)) {
            return;
        }
        const focusError = e.detail.focusError === true;
        this.activePanel = panel;
        requestAnimationFrame(() => this.landInPanel(panel, focusError, LAND_WAIT_FRAMES));
    },
    // Scroll to and focus the opened panel's first error, its alert or its header,
    // once no panel is still opening or closing. While the panel above is closing,
    // everything below it moves up, and on a phone that left the error off
    // screen. _x_transitioning is Alpine's marker for a running transition,
    // which x-collapse sets on a panel until it has finished opening or closing.
    landInPanel(panel, focusError, framesLeft) {
        const moving = [...this.$root.querySelectorAll('section[id^="panel-"]')]
            .some((section) => section._x_transitioning);
        if (moving && framesLeft > 0) {
            requestAnimationFrame(() => this.landInPanel(panel, focusError, framesLeft - 1));
            return;
        }
        const id = panel.replaceAll('.', '-');
        const section = focusError ? document.getElementById('panel-' + id) : null;
        // Two lookups, not one selector list: the alert sits above the fields, so a
        // list would pick it by document order over "Username cannot be empty."
        const error = section?.querySelector('[data-input-error]')
            ?? section?.querySelector('[data-panel-error]');
        const target = error ?? document.getElementById('header-' + id);
        target?.scrollIntoView({ block: 'center' });
        // Move focus so keyboard users land in the panel and screen readers
        // announce it; preventScroll keeps the centered scroll.
        target?.focus({ preventScroll: true });
    },
    setActivePanel(panel) {
        if(this.activePanel === panel) {
            this.activePanel = '';
            return;
        }
        this.activePanel = panel;
    },
    // Move focus to a panel header. The Blade component renders header ids as
    // `header-{view}` with dots replaced by dashes (str_replace('.', '-', $view)),
    // so mirror that here or getElementById misses. Bail instead of calling
    // .focus() on null when there is no matching header (e.g. no active panel).
    focusPanelHeader(panel) {
        if (panel === undefined || panel === '') {
            return;
        }
        const header = document.getElementById(`header-${String(panel).replace(/\./g, '-')}`);
        if (!header) {
            return;
        }
        this.activePanel = panel;
        header.focus();
    },
    focusPreviousPanel(event) {
        if (event.key === 'ArrowUp') {
            const currentIndex = this.panels.indexOf(this.activePanel);
            const previousPanel = this.panels[currentIndex <= 0 ? this.panels.length - 1 : currentIndex - 1];
            this.focusPanelHeader(previousPanel);
            event.stopPropagation();
            event.preventDefault();
        }
    },
    focusNextPanel(event) {
        if (event.key === 'ArrowDown') {
            const currentIndex = this.panels.indexOf(this.activePanel);
            const nextPanel = this.panels[currentIndex === -1 || currentIndex === this.panels.length - 1 ? 0 : currentIndex + 1];
            this.focusPanelHeader(nextPanel);
            event.stopPropagation();
            event.preventDefault();
        }
    },
    focusFirstPanel(event) {
        if (event.key === 'Home') {
            this.focusPanelHeader(this.panels[0]);
            event.stopPropagation();
            event.preventDefault();
        }
    },
    focusLastPanel(event) {
        if (event.key === 'End') {
            this.focusPanelHeader(this.panels[this.panels.length - 1]);
            event.stopPropagation();
            event.preventDefault();
        }
    },
});
